import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Full Property-Tax email composer (opened from the ✉ icon in the Property Tax tab): sender, recipient,
// Cc, SUBJECT and BODY are all pre-filled from TPL-023 and fully editable. The uploaded property-bureau
// document(s) for that year can be attached (checkbox per file). Mirrors ReceiptEmailComposer.

const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

function EmailChips({ value, onChange, ph }: { value: string[]; onChange: (v: string[]) => void; ph: string }) {
  const [q, setQ] = useState('');
  const add = (raw: string) => { const v = raw.trim().toLowerCase(); if (v && validEmail(v) && !value.includes(v)) onChange([...value, v]); setQ(''); };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '6px 8px', border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)' }}>
      {value.map(e => (
        <span key={e} style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8, background: 'var(--accent-bg)', color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {e}<span onClick={() => onChange(value.filter(x => x !== e))} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
        </span>
      ))}
      <input value={q} onChange={e => setQ(e.target.value)} placeholder={ph}
        onKeyDown={e => { if ((e.key === 'Enter' || e.key === ',') && q.trim()) { e.preventDefault(); add(q); } }}
        onBlur={() => q.trim() && add(q)}
        style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}

type DocOpt = { id: string; filename: string; mime: string };

export default function PropertyTaxEmailComposer({ clientId, year, mode = 'request', onClose, onSent }: {
  clientId: string; year: number; mode?: 'request' | 'receipt' | 'payment'; onClose: () => void; onSent?: () => void;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [company, setCompany] = useState('');
  const [docs, setDocs] = useState<DocOpt[]>([]);
  const [attach, setAttach] = useState<Record<string, boolean>>({});
  const [warning, setWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let alive = true;
    const load = mode === 'receipt' ? api.preparePropTaxReceiptEmail(clientId, year)
      : mode === 'payment' ? api.preparePropTaxPaymentEmail(clientId, year)
      : api.preparePropTaxEmail(clientId, year);
    load.then((d: any) => {
      if (!alive) return;
      setFrom(d.from || ''); setTo(d.to || []); setCc(d.cc || []);
      setSubject(d.subject || ''); setInitialBody(d.bodyHtml || ''); setCompany(d.company || '');
      const dd: DocOpt[] = d.availableDocs || [];
      setDocs(dd); setAttach(Object.fromEntries(dd.map(x => [x.id, true])));   // attach all by default
      setWarning(d.warning || '');
    }).catch((e: any) => alive && setErr(e?.message || 'Failed to prepare email')).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [clientId, year, mode]);

  // The "receipt received" email MUST carry the official receipt — block sending until one is attached.
  const checkedDocIds = docs.filter(d => attach[d.id]).map(d => d.id);
  const receiptBlocked = mode === 'receipt' && checkedDocIds.length === 0;

  const send = async () => {
    const body = bodyRef.current ? bodyRef.current.innerHTML : initialBody;
    if (!to.length) { setErr(t('iv_email_need')); return; }
    if (receiptBlocked) { setErr(t('pt_receipt_need_attach')); return; }
    setBusy(true); setErr('');
    const attachmentDocIds = checkedDocIds;
    try { await api.sendPropTaxEmailCustom(clientId, { year, from, to, cc, subject, bodyHtml: body, attachmentDocIds }); setDone(true); onSent?.(); setTimeout(onClose, 1200); }
    catch (e: any) { setErr(e?.message || 'Failed to send'); } finally { setBusy(false); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 13, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', display: 'block', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 600, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{mode === 'receipt' ? t('pt_receipt_email_title') : mode === 'payment' ? t('pt_payment_email_title') : t('pt_email_title')}</div>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-bg)', padding: '4px 11px', borderRadius: 999 }}>{year}{company ? ` · ${company}` : ''}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('iv_email_cc')}</div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} /></div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {warning && <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', borderRadius: 9, padding: '8px 11px', marginBottom: 12 }}><i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{warning}</div>}

            <label style={lbl}>{t('bie_from')}</label>
            <input value={from} onChange={e => setFrom(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_to')}</label>
            <EmailChips value={to} onChange={setTo} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_cc')}</label>
            <EmailChips value={cc} onChange={setCc} ph={t('bie_email_ph')} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_subject')}</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />

            <label style={{ ...lbl, marginTop: 13 }}>{t('bie_body')}</label>
            <div ref={bodyRef} contentEditable suppressContentEditableWarning
              dangerouslySetInnerHTML={{ __html: initialBody }}
              style={{ minHeight: 200, maxHeight: 340, overflowY: 'auto', padding: '11px 13px', fontSize: 13.5, lineHeight: 1.6, border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none' }} />

            {mode !== 'payment' && (<>
            <label style={{ ...lbl, marginTop: 13 }}>{mode === 'receipt' ? t('pt_receipt_attach') : t('pt_email_attach')}</label>
            {receiptBlocked && (
              <div style={{ fontSize: 12.5, color: '#92400E', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 9, padding: '9px 12px', marginBottom: 8, fontWeight: 600 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />{t('pt_receipt_need_attach')}
              </div>
            )}
            {docs.length === 0 ? (
              mode === 'receipt' ? null : <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('pt_email_no_docs')}</div>
            ) : docs.map(d => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-primary)', padding: '3px 0' }}>
                <input type="checkbox" checked={!!attach[d.id]} onChange={e => setAttach(a => ({ ...a, [d.id]: e.target.checked }))} style={{ cursor: 'pointer', flexShrink: 0 }} />
                <i className={(d.mime || '').includes('pdf') ? 'ti ti-file-type-pdf' : 'ti ti-photo'} style={{ color: (d.mime || '').includes('pdf') ? '#E24B4A' : 'var(--accent)', fontSize: 16, flexShrink: 0 }} />
                <span onClick={() => api.clientOpenDoc(d.id)} title={`${d.filename} — preview`} style={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</span>
                <button onClick={() => api.clientOpenDoc(d.id)} title="Preview" style={{ flexShrink: 0, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 15 }}><i className="ti ti-eye" /></button>
              </div>
            ))}
            </>)}

            {err && <div style={{ fontSize: 12.5, color: '#E24B4A', marginTop: 12 }}>{err}</div>}
            {done ? (
              <div style={{ fontSize: 13.5, color: '#15803D', fontWeight: 700, marginTop: 16 }}><i className="ti ti-circle-check" style={{ marginRight: 6 }} />{t('iv_email_sent')}</div>
            ) : (
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
                <button onClick={send} disabled={busy || receiptBlocked} title={receiptBlocked ? t('pt_receipt_need_attach') : ''} style={{ padding: '9px 22px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 800, fontSize: 13.5, cursor: (busy || receiptBlocked) ? 'not-allowed' : 'pointer', opacity: (busy || receiptBlocked) ? 0.55 : 1 }}>{busy ? t('login_wait') : t('iv_email_send')}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
